import { ToastAria, useToast } from "@react-aria/toast";
import { DOMProps } from "@react-types/shared";
import { Button, ToastNotificationProps } from "ds/ui";
import {
  CheckCircleIcon,
  CircleAlertIcon,
  CircleSlashIcon,
  InfoIcon,
  XIcon,
} from "lucide-react";
import { ElementType, useRef } from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { useTranslations } from "translations";

type ToastVariant = VariantProps<typeof toastVariants>;
type ToastColor = Exclude<ToastVariant["color"], undefined>;

export type ToastContentType = DOMProps & {
  action?: {
    label: string;
    onClick?: () => void;
  };
  color: ToastColor;
  message: string;
};

const toastVariants = tv({
  defaultVariants: {
    color: "primary",
  },
  slots: {
    actions: "flex items-center gap-2",
    base: "flex items-center px-4 py-2 tablet:p-4 rounded-lg shadow-md text-body-sm gap-2 tablet:min-w-[400px] max-w-[600px] justify-between ",
    closeIcon: "h-5 w-5 flex-shrink-0",
    content: "flex items-center gap-4",
    icon: "h-5 w-5 flex-shrink-0 hidden tablet:block",
  },
  variants: {
    color: {
      danger: "bg-danger text-danger-foreground",
      primary: "bg-primary text-primary-foreground",
      success: "bg-success text-success-foreground",
      warning: "bg-warning text-warning-foreground",
    },
  },
});

export const ICONS: Record<ToastColor, ElementType> = {
  danger: CircleSlashIcon,
  primary: InfoIcon,
  success: CheckCircleIcon,
  warning: CircleAlertIcon,
};

export const ToastContent = ({
  closeButtonProps,
  state,
  titleProps,
  toast: {
    content: { action, color, message },
    key,
  },
}: ToastNotificationProps<ToastContentType> &
  Pick<ToastAria, "closeButtonProps" | "titleProps">) => {
  const translations = useTranslations();
  const Icon = ICONS[color];
  const { actions, base, closeIcon, content, icon } = toastVariants({ color });

  return (
    <div className={base()} data-testid="notification-message">
      <div className={content()}>
        <Icon className={icon()} />
        <p {...titleProps}>{message}</p>
      </div>
      <div className={actions()}>
        {action && (
          <Button
            color={color}
            id="notification-message-action"
            size="sm"
            variant="solid"
            onPress={action.onClick}
          >
            {action.label}
          </Button>
        )}
        <Button
          {...closeButtonProps}
          aria-label={translations.actions.close}
          color={color}
          id="notification-close"
          isIconOnly
          size="xs"
          variant="solid"
          onPress={() => state.close(key)}
        >
          <XIcon className={closeIcon()} />
        </Button>
      </div>
    </div>
  );
};

export const Toast = ({
  state,
  ...props
}: ToastNotificationProps<ToastContentType>) => {
  const ref = useRef(null);
  const { closeButtonProps, contentProps, titleProps, toastProps } = useToast(
    props,
    state,
    ref,
  );

  return (
    <div ref={ref} {...toastProps}>
      <div {...contentProps}>
        <ToastContent
          closeButtonProps={closeButtonProps}
          state={state}
          titleProps={titleProps}
          toast={props.toast}
        />
      </div>
    </div>
  );
};

Toast.displayName = "RecareUI.Toast";
